import React, {useCallback, useEffect, useState} from "react";
import {CircularProgress, MenuItem, TextField, Typography} from "@mui/material";
import Axios from "../../util/Axios";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Blue3, Grey2, Grey6, Grey7, Grey17} from "../../theme/styles";

/**
 * Which object types offer this form when somebody creates a record.
 *
 * THE LINK IS TO THE LOGICAL FORM, not to a revision — so it survives every Revise, which is the
 * whole reason a form has revisions. That is why this needs FormUSID and not the revision on screen.
 *
 * "STOP OFFERING" CLOSES, IT DOES NOT DELETE. The row is kept with an end date, because it is the
 * only trail back from a record to the association that offered it. The closed list below is that
 * trail, shown rather than hidden — a form that used to be on three types and is now on one is a
 * fact somebody will eventually need.
 *
 * RECORDS ALREADY CREATED ARE UNAFFECTED, and the screen says so where the action is, not in a
 * footnote. Each record carries its own copy of the form it was seeded from.
 */

type Link = {
    USID: string;
    ObjectTypeUSID: string;
    Name?: string;
    Acronym?: string;
    ObjectClassification?: string;
    Active?: boolean;
    ValidFrom?: string;
    ValidTo?: string | null;
};

type ObjectType = {USID?: string; Name?: string; Acronym?: string; ObjectClassification?: string};

type Props = {
    formUSID: string | null;
    /** Only to word the empty state honestly — a draft can be linked, it is just not offered yet. */
    status?: string;
};

export default function FormLinks({formUSID, status}: Props) {
    const [links, setLinks] = useState<Link[]>([]);
    const [closed, setClosed] = useState<Link[]>([]);
    const [types, setTypes] = useState<ObjectType[]>([]);
    const [chosen, setChosen] = useState("");
    const [confirming, setConfirming] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showHistory, setShowHistory] = useState(false);

    const load = useCallback(() => {
        if (!formUSID) return;
        setLoading(true);
        Axios({method: "GET", url: "t1/v1/form_links", params: {FormUSID: formUSID}})
            .then((response: any) => {
                setLinks(response.data?.Links ?? []);
                setClosed(response.data?.Closed ?? []);
                setError(null);
                setLoading(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "The object types could not be loaded.");
                setLoading(false);
            });
    }, [formUSID]);

    useEffect(load, [load]);

    useEffect(() => {
        // Every classification's types, not just Documents — a form can be offered on any of them.
        Axios({method: "GET", url: "t1/v1/object_types", params: {Active: "true", Limit: 300}})
            .then((r: any) => {
                const list = r.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
                setTypes(Array.isArray(list) ? list : [list]);
            })
            .catch(() => setTypes([]));
    }, []);

    const linked = new Set(links.map(l => l.ObjectTypeUSID));
    const available = types.filter(t => t.USID && !linked.has(t.USID));

    const offer = () => {
        setBusy(true);
        Axios({method: "POST", url: "t1/v1/form_links",
               data: {FormUSID: formUSID, ObjectTypeUSID: chosen}})
            .then(() => { setChosen(""); setBusy(false); load(); })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This form could not be offered on that type.");
                setBusy(false);
            });
    };

    const stop = (objectTypeUSID: string) => {
        setBusy(true);
        Axios({method: "DELETE", url: "t1/v1/form_links",
               params: {FormUSID: formUSID, ObjectTypeUSID: objectTypeUSID}})
            .then(() => { setConfirming(null); setBusy(false); load(); })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This form could not be stopped on that type.");
                setBusy(false);
            });
    };

    if (!formUSID) {
        return <Typography style={{fontSize: 14, color: Grey7, marginTop: 24}}>
            This form has no object yet, so it cannot be offered anywhere.
        </Typography>;
    }

    return (
        <div style={{marginTop: 20, maxWidth: 720, overflowY: "auto"}}>
            <Typography style={{fontSize: 13, color: Grey7, marginBottom: 16}}>
                When somebody creates a record of one of these object types, this form is offered as
                one of the ways to fill it in.
                {status !== "RELEASED" && (
                    <> A {String(status ?? "draft").toLowerCase()} form can be linked now and is
                    offered once it is released.</>
                )}
            </Typography>

            {error && <Typography style={{fontSize: 13, color: "#B3261E", marginBottom: 10}}>{error}</Typography>}
            {loading && <CircularProgress size={18}/>}

            {!loading && links.length === 0 && (
                <Typography style={{fontSize: 14, color: Grey7, marginBottom: 16}}>
                    Not offered anywhere yet.
                </Typography>
            )}

            {links.map(link => (
                <div key={link.USID}
                     style={{display: "flex", alignItems: "center", gap: 12, border: `1px solid ${Grey2}`,
                             borderLeft: `3px solid ${Blue3}`, borderRadius: 3, padding: "10px 14px",
                             marginBottom: 8, background: "#fff"}}>
                    <div style={{flex: 1, minWidth: 0}}>
                        <Typography style={{fontSize: 14, color: Grey17, fontWeight: 500}}>
                            {link.Name}
                        </Typography>
                        <Typography style={{fontSize: 12, color: Grey6}}>
                            {link.ObjectClassification}{link.Acronym ? ` · ${link.Acronym}` : ""}
                            {link.Active === false ? " · inactive type" : ""}
                        </Typography>
                    </div>
                    {confirming === link.ObjectTypeUSID ? (
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            <Typography style={{fontSize: 12, color: Grey6, maxWidth: 260}}>
                                New records will not be offered this form. Records already created keep working.
                            </Typography>
                            <CCButton className={css_buttons.blueStandardButton} disabled={busy}
                                      onClick={() => stop(link.ObjectTypeUSID)}>Stop</CCButton>
                            <CCButton className={css_buttons.whiteStandardButton}
                                      onClick={() => setConfirming(null)}>Cancel</CCButton>
                        </div>
                    ) : (
                        <CCButton className={css_buttons.whiteStandardButton} disabled={busy}
                                  onClick={() => setConfirming(link.ObjectTypeUSID)}>Stop offering</CCButton>
                    )}
                </div>
            ))}

            <div style={{display: "flex", alignItems: "flex-end", gap: 10, marginTop: 18}}>
                <TextField select size="small" label="Offer on" value={chosen} sx={{minWidth: 320}}
                           onChange={e => setChosen(e.target.value)}
                           disabled={busy || available.length === 0}
                           helperText={available.length === 0
                               ? "Every object type already offers this form."
                               : "The object type whose records this form should fill in."}>
                    {available.map(t => (
                        <MenuItem key={t.USID} value={t.USID}>
                            {t.Name} · {t.ObjectClassification}
                        </MenuItem>
                    ))}
                </TextField>
                <CCButton className={css_buttons.blueStandardButton} disabled={busy || !chosen}
                          onClick={offer}>Add</CCButton>
            </div>

            {closed.length > 0 && (
                <div style={{marginTop: 28}}>
                    <Typography onClick={() => setShowHistory(h => !h)}
                                style={{fontSize: 12, fontWeight: 700, color: Grey6,
                                        textTransform: "uppercase", letterSpacing: "0.5px",
                                        cursor: "pointer"}}>
                        {showHistory ? "▾" : "▸"} No longer offered ({closed.length})
                    </Typography>
                    {showHistory && (
                        <>
                            {/* Kept rather than deleted: a record's schema is a copy of the form it
                                was seeded from, and this is the only trail back to the association
                                that offered it. */}
                            <Typography style={{fontSize: 12, color: Grey6, margin: "8px 0 10px"}}>
                                Kept so a record created through one of these can still be traced back to it.
                            </Typography>
                            {closed.map(link => (
                                <div key={link.USID}
                                     style={{display: "flex", gap: 12, border: `1px solid ${Grey2}`,
                                             borderRadius: 3, padding: "8px 14px", marginBottom: 6,
                                             background: "#fafafa"}}>
                                    <Typography style={{fontSize: 13, color: Grey7, flex: 1}}>
                                        {link.Name}
                                    </Typography>
                                    <Typography style={{fontSize: 12, color: Grey6}}>
                                        until {String(link.ValidTo ?? "").slice(0, 10)}
                                    </Typography>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
